import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { SudoButton } from "../../../sudokit/components/SudoButton";
import { Spinner } from "../../../sudokit/components/Spinner";
import { isHomePath } from "../../../utils/isHomePath";

export const FOLDER_OPEN_DELAY_MS = 5000;

export default function OpeningFolderToast({ isSeries = false, onOpen }) {
  const location = useLocation();
  const onOpenRef = useRef(onOpen);
  const hasOpenedRef = useRef(false);
  const pathnameRef = useRef(location.pathname);
  const cancelledAutoOpenRef = useRef(!isHomePath(location.pathname));
  const [leftHome, setLeftHome] = useState(() => !isHomePath(location.pathname));

  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);

  useEffect(() => {
    pathnameRef.current = location.pathname;
    if (isHomePath(location.pathname)) return;
    cancelledAutoOpenRef.current = true;
    setLeftHome(true);
  }, [location.pathname]);

  const openFolder = useCallback((options) => {
    if (hasOpenedRef.current) return;
    // Latch before calling so double-clicks can't navigate twice; roll back if
    // onOpen declines (e.g. auto-open when the user already left home).
    hasOpenedRef.current = true;
    const opened = onOpenRef.current(options);
    if (opened === false) {
      hasOpenedRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (leftHome) return undefined;

    const timeoutId = window.setTimeout(() => {
      if (cancelledAutoOpenRef.current) return;
      if (!isHomePath(pathnameRef.current)) return;
      openFolder({ auto: true });
    }, FOLDER_OPEN_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [leftHome, openFolder]);

  if (leftHome) {
    return (
      <div className="flex items-center gap-3">
        <span>{isSeries ? "Series folder ready" : "Folder ready"}</span>
        <SudoButton
          variant="invertedColorSecondary"
          size="default"
          className="!text-[var(--context-menu-text)]"
          onClick={() => openFolder()}
        >
          Open
        </SudoButton>
      </div>
    );
  }

  if (isSeries) {
    return (
      <div className="flex items-center gap-2">
        <Spinner
          size="sm"
          label="Setting up your series folder..."
          className="!border-[var(--context-menu-text)] !border-t-transparent"
        />
        <span aria-hidden="true">Setting up your series folder...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <span>Opening folder</span>
      <SudoButton
        state="loading"
        variant="invertedColorSecondary"
        size="default"
        loadingPercentage={90}
        loadingTransitionDuration={FOLDER_OPEN_DELAY_MS / 1000}
        onClick={() => openFolder()}
      >
        Go
      </SudoButton>
    </div>
  );
}
